// ─────────────────────────────────────────────────────────────
// HorizontalDashboard
//
// Three-column layout alternative to Dashboard.js.
// Accepts the exact same props as Dashboard — no new data
// fetching, no new hooks. Everything is reused from the
// existing widget/hook layer.
//
// Column layout:
//   Left  (240px) — Today's Plan + KPI strip + Today's Performance
//   Center (flex) — Welcome + Account selector + Quick actions +
//                   Account Equity + ROI Summary + Recent Trades
//   Right  (280px) — Economic Calendar + Market Sentiment + News
//
// Enabled via profiles.dashboard_layout = 'horizontal'.
// Switch lives in ProfileSettings → Appearance section.
//
// Calendar data is fetched ONCE here via useCalendar and passed
// as props to MarketStatusCard and EconomicCalendarWidget so
// both widgets always reference the same event set.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import { Area, AreaChart, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { SetupBanner } from "../../components/layout/SetupBanner";
import { SubscriptionBanners } from "../billing/SubscriptionBanners";
import { Card } from "../../components/ui/Card";
import { Pill } from "../../components/ui/Pill";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { Tag } from "../../components/ui/Tag";
import { EconomicCalendarWidget } from "../../components/widgets/EconomicCalendarWidget";
import { MarketSentimentWidget } from "../../components/widgets/MarketSentimentWidget";
import { MarketStatusCard } from "../../components/widgets/MarketStatusCard";
import { NewsWidget } from "../../components/widgets/NewsWidget";
import { TraderSentimentWidget } from "../../components/widgets/TraderSentimentWidget";
import { TodaysPerformanceCard } from "../../components/widgets/TodaysPerformanceCard";
import { PropFirmComplianceWidget, FIRM_COMPLIANCE_PRESETS } from "../../components/widgets/PropFirmComplianceWidget";
import { TodaysPlanCard } from "../../components/widgets/TodaysPlanCard";
import { ROISummaryWidget } from "../../components/widgets/ROISummaryWidget";
import { useCalendar } from "../../hooks/useCalendar";
import { useUserRules } from "../../hooks/useUserRules";
import { buildAnalytics, computeWinRate } from "../../lib/analytics";
import { C } from "../../lib/colors";
import { fmtPct, fmt$, isSessionShifted, getActiveSessionDate } from "../../lib/format";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";
import { TradeShareModal } from "../landing/TradeShareModal";

export function HorizontalDashboard({
  trades,
  setView,
  showSetup,
  setShowSetup,
  displayName = "Trader",
  accounts = [],
  currentAccountId,
  setCurrentAccountId,
  profile,
  openTradeReview,
  updateTrade,
  userId,
}) {
  const [showRMultiple, setShowRMultiple] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);

  // Archived accounts (blown or passed-into-funded) are hidden from the
  // Viewing Account selector — reviewable from Portfolio's archive
  // sections instead of cluttering the daily-use dropdown.
  const activeAccounts = Array.isArray(accounts)
    ? accounts.filter(a => !a.is_blown && !a.is_passed)
    : [];

  // ── Single calendar fetch for the whole dashboard ──────────────────────────
  const {
    todayEvents, eventsByDay, marketStatus, nextHighImpact, calLoading,
    dataTrustworthy, calError,
  } = useCalendar();

  // Fetch payouts so equity reflects actual withdrawals
  const [payouts, setPayouts] = useState([]);
  useEffect(() => {
    if (!userId) return;
    authFetch(`${SUPABASE_URL}/rest/v1/payouts?user_id=eq.${userId}&select=account_id,amount,date`)
      .then(r => r.json())
      .then(data => setPayouts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [userId]);

  // Mobile/orientation detection for responsive column visibility
  const [mobileView, setMobileView] = useState(() => {
    if (typeof window === "undefined") return "desktop";
    const w = window.innerWidth;
    const h = window.innerHeight;
    if (w >= 1024) return "desktop";
    return w > h ? "landscape" : "portrait";
  });

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (w >= 1024) setMobileView("desktop");
      else if (w > h) setMobileView("landscape");
      else setMobileView("portrait");
    };
    const onReset = () => {
      setLeftW(DEFAULT_LEFT);
      setRightW(DEFAULT_RIGHT);
    };
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    window.addEventListener("fos_reset_cols", onReset);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      window.removeEventListener("fos_reset_cols", onReset);
    };
  }, []);

  // ── Resizable columns (desktop only) ────────────────────────────────────
  const DEFAULT_LEFT  = 372;
  const DEFAULT_RIGHT = 348;
  const MIN_COL       = 160;

  const savedWidths = (() => {
    try {
      const s = localStorage.getItem("fos_dash_cols");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  })();

  const [leftW,  setLeftW]  = useState(savedWidths?.left  ?? DEFAULT_LEFT);
  const [rightW, setRightW] = useState(savedWidths?.right ?? DEFAULT_RIGHT);

  const persistWidths = useCallback((l, r) => {
    try { localStorage.setItem("fos_dash_cols", JSON.stringify({ left: l, right: r })); }
    catch {}
  }, []);

  const resetWidths = () => {
    setLeftW(DEFAULT_LEFT);
    setRightW(DEFAULT_RIGHT);
    persistWidths(DEFAULT_LEFT, DEFAULT_RIGHT);
  };

  // Plain drag handler factory (not a hook — avoids rules-of-hooks violation)
  const makeDragHandler = (onDrag) => (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    let lastX = e.clientX;
    const onMove = (ev) => {
      onDrag(ev.clientX - lastX);
      lastX = ev.clientX;
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
  };

  const leftHandleDown = makeDragHandler((delta) => {
    setLeftW(prev => {
      const next = Math.max(MIN_COL, prev + delta);
      persistWidths(next, rightW);
      return next;
    });
  });

  const rightHandleDown = makeDragHandler((delta) => {
    setRightW(prev => {
      const next = Math.max(MIN_COL, prev - delta);
      persistWidths(leftW, next);
      return next;
    });
  });

  const [chartView, setChartView] = useState("equity");
  const [timeRange, setTimeRange] = useState("All");
  const [showPayouts, setShowPayouts] = useState(false);
  const [showDrawdownLine, setShowDrawdownLine] = useState(false);
  const [showDailyLossLine, setShowDailyLossLine] = useState(false);
  const [showTargetGoalLine, setShowTargetGoalLine] = useState(false);

  // Local account selection — same pattern as Dashboard.js
  const [dashboardAccountId, setDashboardAccountId] = useState(
    () => currentAccountId || null
  );

  const selectedAccount = Array.isArray(accounts)
    ? accounts.find(a => a.id === dashboardAccountId)
    : null;

  const accountTrades = dashboardAccountId && Array.isArray(accounts)
    ? trades.filter(t => t.account_id === dashboardAccountId)
    : trades;

  const today       = getActiveSessionDate();
  const todayTrades = accountTrades.filter(
    t => String(t.trade_date).slice(0, 10) === today
  );
  const todayWR  = computeWinRate(todayTrades);
  const winRate  = todayWR.winRate;
  const analytics = buildAnalytics(accountTrades);

  // Equity
  const startingBalance = Array.isArray(accounts)
    ? (dashboardAccountId
        ? parseFloat(accounts.find(a => a.id === dashboardAccountId)?.starting_balance || 0)
        : accounts.reduce((s, a) => s + parseFloat(a.starting_balance || 0), 0))
    : 0;
  const netPnl      = analytics?.netPnl ?? 0;
  const totalPayouts = payouts
    .filter(p => !dashboardAccountId || p.account_id === dashboardAccountId)
    .reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const totalEquity = startingBalance + netPnl - totalPayouts;

  const acctDrawdownLimit  = parseFloat(selectedAccount?.drawdown_limit  || 0);
  const acctDailyLossLimit = parseFloat(selectedAccount?.daily_loss_limit || 0);
  const dailyLossThreshold = chartView === "equity"
    ? startingBalance - acctDailyLossLimit
    : -acctDailyLossLimit;

  // Target Goal — only meaningful for Prop Eval accounts (the amount
  // required to pass). Stored in the same profit_target column used
  // elsewhere (PropFirmComplianceWidget, evalSimulator).
  const isEvalAccount = selectedAccount?.account_type === "prop";
  const acctTargetGoal = isEvalAccount ? parseFloat(selectedAccount?.profit_target || 0) : 0;
  const targetGoalThreshold = chartView === "equity"
    ? startingBalance + acctTargetGoal
    : acctTargetGoal;

  const sortedPayoutTotals = (() => {
    if (!showPayouts) return [];
    const accountPayouts = payouts
      .filter(p => !dashboardAccountId || p.account_id === dashboardAccountId)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    let cumulative = 0;
    return accountPayouts.map(p => {
      cumulative += parseFloat(p.amount || 0);
      // Store the full ISO date, not a year-stripped "MM-DD" — ISO date
      // strings sort/compare correctly across year boundaries, unlike
      // MM-DD alone (see analytics.js equityCurve comment for why this
      // matters: a Feb-2026 payout must never be treated as occurring
      // before a Nov-2025 trade just because "02" < "11" as strings).
      const fullDate = String(p.date).slice(0, 10);
      return { fullDate, cumulative };
    });
  })();

  // curveDate must be a full ISO date ("YYYY-MM-DD"). ISO strings compare
  // correctly with simple <=, so no Date parsing (and its year-less-string
  // ambiguity) is needed.
  const payoutDeductionAsOf = (curveDate) => {
    if (sortedPayoutTotals.length === 0) return 0;
    const fullDate = String(curveDate).slice(0, 10);
    let result = 0;
    for (const sp of sortedPayoutTotals) {
      if (sp.fullDate <= fullDate) result = sp.cumulative;
      else break;
    }
    return result;
  };

  // Resolve drawdown limit — account column first, then firm preset
  const resolvedDrawdownLimit = (() => {
    if (acctDrawdownLimit > 0) return acctDrawdownLimit;
    if (!selectedAccount) return 0;
    const firm = selectedAccount.prop_firm || "";
    const bal  = String(Math.round(parseFloat(selectedAccount.starting_balance || 0)));
    const preset = FIRM_COMPLIANCE_PRESETS[firm];
    return parseFloat((preset?.[bal] || preset?.default)?.drawdown_limit || 0);
  })();

  const resolvedDrawdownType = selectedAccount?.drawdown_type || "eod_trailing";

  // Trailing-drawdown lock offset above starting balance, by prop firm.
  // Verified against each firm's own drawdown documentation (Aug 2026):
  //  - Apex ("Safety Net"): locks at starting balance + drawdown + $100
  //  - MyFundedFutures: locks at starting balance + $100
  //  - Bulenox (Master Account): locks at starting balance + $100
  //  - TopStep: locks exactly at starting balance (no buffer)
  //  - Take Profit Trader (PRO Account Rules, "Intraday Trailing Drawdown"):
  //      locks exactly at starting balance (no buffer)
  // Firms not listed default to $0 (locks at exactly starting balance).
  const DRAWDOWN_LOCK_OFFSET_BY_FIRM = {
    "Apex": 100,
    "MyFundedFutures": 100,
    "Bulenox": 100,
    "TopStep": 0,
    "Take Profit Trader": 0,
  };
  const drawdownLockOffset = DRAWDOWN_LOCK_OFFSET_BY_FIRM[selectedAccount?.prop_firm] ?? 0;

  // Build per-day trailing floor curve for the chart
  const drawdownFloorCurve = (() => {
    if (!showDrawdownLine || resolvedDrawdownLimit <= 0) return [];

    if (resolvedDrawdownType === "intraday_trailing") {
      // Build from analytics.equityCurve — already in correct trade order
      const curve = analytics?.equityCurve || [];
      let peak = startingBalance;
      // The trailing floor stops moving permanently once peak balance first
      // reaches Start + DD + lockOffset, locking at Start + lockOffset from
      // that point on — it does NOT keep trailing upward indefinitely.
      const lockTriggerPeak = startingBalance + resolvedDrawdownLimit + drawdownLockOffset;
      const lockedFloorValue = startingBalance + drawdownLockOffset;
      let locked = false;
      return curve.map(p => {
        // Peak must reflect actual account balance (net of payouts), not raw
        // trading equity — otherwise the floor chases a peak that's too high
        // once withdrawals are taken.
        const bal = startingBalance + p.v - payoutDeductionAsOf(p.fullDate);
        if (bal > peak) peak = bal;
        if (!locked && peak >= lockTriggerPeak) locked = true;
        const floor = locked ? lockedFloorValue : peak - resolvedDrawdownLimit;
        return { date: p.date, floor: chartView === "equity" ? floor : floor - startingBalance };
      });
    }

    // EOD trailing or static — one floor point per day
    const byDate = {};
    accountTrades.forEach(t => {
      const d = String(t.trade_date || "").slice(0, 10);
      if (!d) return;
      if (!byDate[d]) byDate[d] = 0;
      byDate[d] += (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
    });
    const sortedDays = Object.keys(byDate).sort();
    let runningBalance = startingBalance;
    let peakBalance    = startingBalance;
    // Same permanent-lock rule as intraday_trailing, applied at EOD
    // granularity: once peak (net of payouts) reaches Start + DD + lockOffset,
    // the floor freezes at Start + lockOffset for good.
    const lockTriggerPeak = startingBalance + resolvedDrawdownLimit + drawdownLockOffset;
    const lockedFloorValue = startingBalance + drawdownLockOffset;
    let locked = false;
    return sortedDays.map(date => {
      runningBalance += byDate[date];
      // Net out payouts so the floor tracks real account balance, not gross
      // trading P&L — otherwise the floor overstates the peak whenever a
      // payout has been taken, and can visually converge on or cross the
      // (payout-adjusted) equity line.
      const netBalance = runningBalance - payoutDeductionAsOf(date);
      if (resolvedDrawdownType === "eod_trailing" && netBalance > peakBalance) {
        peakBalance = netBalance;
      }
      if (resolvedDrawdownType === "eod_trailing" && !locked && peakBalance >= lockTriggerPeak) {
        locked = true;
      }
      const floor = resolvedDrawdownType === "static"
        ? startingBalance - resolvedDrawdownLimit
        : (locked ? lockedFloorValue : peakBalance - resolvedDrawdownLimit);
      return { date: date.slice(5), floor: chartView === "equity" ? floor : floor - startingBalance };
    });
  })();

  // Equity curve time-range filter — identical logic to Dashboard.js
  const filteredEquityCurve = (() => {
    const curve = analytics?.equityCurve || [];
    if (timeRange === "All" || curve.length === 0) return curve;
    const sortedTrades = [...accountTrades].sort((a, b) =>
      new Date(a.trade_date + " " + (a.entry_time || a.trade_time || "00:00")) -
      new Date(b.trade_date + " " + (b.entry_time || b.trade_time || "00:00"))
    );
    const cutoff = new Date();
    if      (timeRange === "1W")  cutoff.setDate(cutoff.getDate() - 7);
    else if (timeRange === "1M")  cutoff.setMonth(cutoff.getMonth() - 1);
    else if (timeRange === "3M")  cutoff.setMonth(cutoff.getMonth() - 3);
    else if (timeRange === "YTD") cutoff.setMonth(0, 1);
    cutoff.setHours(0, 0, 0, 0);
    let cutoffIdx = sortedTrades.findIndex(t => new Date(t.trade_date) >= cutoff);
    if (cutoffIdx === -1) return [];
    return curve.slice(cutoffIdx);
  })();

  const rangeNetPnl = (() => {
    if (timeRange === "All") return netPnl;
    if (filteredEquityCurve.length === 0) return 0;
    const endV      = filteredEquityCurve[filteredEquityCurve.length - 1].v;
    const fullCurve = analytics?.equityCurve || [];
    const sliceStartIdx = fullCurve.length - filteredEquityCurve.length;
    const baseline  = sliceStartIdx > 0 ? fullCurve[sliceStartIdx - 1].v : 0;
    return endV - baseline;
  })();

  // Rules
  const { rules: accountRules = [] } = useUserRules(userId, dashboardAccountId);
  const maxTradesRule    = (accountRules || []).find(r => r.enabled && r.rule_type === "max_trades_per_day");
  const dailyLimit       = maxTradesRule?.parameters?.max_trades ?? null;
  const drawdownRule     = (accountRules || []).find(r => r.enabled && r.rule_type === "max_daily_drawdown");
  const maxDailyDrawdown = parseFloat(drawdownRule?.parameters?.max_drawdown)
    || parseFloat(profile?.max_daily_drawdown)
    || 400;

  const todayAvgTrade = todayTrades.length > 0
    ? todayTrades.reduce(
        (s, t) => s + (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0),
        0
      ) / todayTrades.length
    : 0;

  const sevenDaysAgo       = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const lastWeekTrades     = accountTrades.filter(t => new Date(t.created_at) >= sevenDaysAgo);
  const lastWeekDiscipline = lastWeekTrades.length > 0
    ? lastWeekTrades.reduce((s, t) => s + (t.discipline_score || 7), 0) / lastWeekTrades.length
    : 0;
  const todayDiscipline  = todayTrades.length > 0
    ? todayTrades.reduce((s, t) => s + (t.discipline_score || 7), 0) / todayTrades.length
    : 0;
  const disciplineTrend  = todayDiscipline - lastWeekDiscipline;
  const isAllAccounts    = !dashboardAccountId;

  const kpis = [
    {
      label: isAllAccounts ? "Avg Trade (All)" : "Avg Trade Today",
      value: todayTrades.length > 0 ? fmt$(todayAvgTrade) : "—",
      sub: todayTrades.length > 0
        ? (isAllAccounts
            ? `across ${todayTrades.length} trade${todayTrades.length === 1 ? "" : "s"}`
            : `${todayTrades.length} trade${todayTrades.length === 1 ? "" : "s"}`)
        : "no trades yet",
      color: todayTrades.length === 0
        ? C.muted
        : todayAvgTrade >= 0 ? C.green : C.red,
    },
    {
      label: isAllAccounts ? "Total Trades Today" : "Trades Today",
      value: `${todayTrades.length}`,
      sub: isAllAccounts
        ? "across all accounts"
        : (dailyLimit != null ? `of ${dailyLimit} max` : "no limit set"),
      color: !isAllAccounts && dailyLimit != null && todayTrades.length >= dailyLimit
        ? C.yellow
        : C.blue,
    },
    { label: "Win Rate Today", value: winRate !== null ? fmtPct(winRate) : "—", color: C.purple },
    {
      label: "Discipline",
      value: analytics
        ? `${Math.round(
            accountTrades.slice(0, 5).reduce((s, t) => s + (t.discipline_score || 7), 0) /
            Math.min(accountTrades.slice(0, 5).length || 1, 5)
          )}/10`
        : "—",
      sub: disciplineTrend > 0.1 ? "📈 Improving" : disciplineTrend < -0.1 ? "📉 Declining" : "→ Stable",
      color: C.green,
      onClick: () => setView("discipline"),
    },
  ];

  // Responsive column config
  const isDesktop   = mobileView === "desktop";
  const isLandscape = mobileView === "landscape";
  const isPortrait  = mobileView === "portrait";

  const gridCols = isPortrait
    ? "1fr"
    : isLandscape
    ? "260px 1fr 240px"
    : `${leftW}px 6px 1fr 6px ${rightW}px`;

  const gridRows  = isPortrait ? "auto auto auto" : "1fr";
  const colHeight = isPortrait ? "auto" : "calc(100vh - 82px)";

  // ── Shared column styles ─────────────────────────────────────────────────
  const colScroll = {
    overflowY: "auto",
    height: isPortrait ? "auto" : "100%",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: "10px 10px",
  };

  return (
    <div style={{ position: "relative" }}>
    <div style={{
      display: "grid",
      gridTemplateColumns: gridCols,
      gridTemplateRows: gridRows,
      gap: 0,
      height: colHeight,
      minHeight: 0,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      overflow: isPortrait ? "visible" : "hidden",
      background: "#0a0c18",
      userSelect: "none",
    }}>

      {/* ── LEFT COLUMN ───────────────────────────────────────────────── */}
      <div style={{
        borderRight: isPortrait ? "none" : `1px solid ${C.border}`,
        borderBottom: isPortrait ? `1px solid ${C.border}` : "none",
        minHeight: 0,
        overflow: "hidden",
      }}>
        <div style={colScroll}>
          <TodaysPlanCard
            userId={userId}
            accountId={dashboardAccountId}
            accountTrades={accountTrades}
            todayEvents={todayEvents}
            calendarTrustworthy={dataTrustworthy}
          />

          <Card style={{ padding: 12 }}>
            <div style={{
              fontSize: 10, color: C.muted, textTransform: "uppercase",
              letterSpacing: "0.12em", fontWeight: 600, marginBottom: 10,
            }}>
              KPIs
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {kpis.map(k => (
                <div
                  key={k.label}
                  onClick={k.onClick}
                  style={{
                    background: "#0f1322",
                    borderRadius: 8,
                    padding: "8px 10px",
                    cursor: k.onClick ? "pointer" : "default",
                  }}
                >
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{k.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: k.color, fontFamily: "monospace" }}>{k.value}</div>
                  {k.sub && <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{k.sub}</div>}
                </div>
              ))}
            </div>
          </Card>

          <TodaysPerformanceCard
            trades={accountTrades}
            maxDailyDrawdown={maxDailyDrawdown}
            startingBalance={startingBalance}
            key={maxDailyDrawdown}
          />

          {selectedAccount && (
            <PropFirmComplianceWidget
              account={selectedAccount}
              trades={accountTrades}
                />
          )}
        </div>
      </div>

      {/* ── DRAG HANDLE: left | center ── desktop only ───────────────── */}
      {isDesktop ? (
        <div
          onMouseDown={leftHandleDown}
          style={{
            cursor: "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = C.border + "40")}
          onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
        >
          <div style={{ width: 2, height: 40, borderRadius: 2, background: C.border, opacity: 0.5 }} />
        </div>
      ) : null}

      {/* ── CENTER COLUMN ─────────────────────────────────────────────── */}
      <div style={{
        minHeight: 0, overflow: "hidden",
        borderRight: isPortrait ? "none" : `1px solid ${C.border}`,
        borderBottom: isPortrait ? `1px solid ${C.border}` : "none",
      }}>
        <div style={colScroll}>

          {showSetup && <SetupBanner onDismiss={() => setShowSetup(false)} />}
          <SubscriptionBanners profile={profile} />

          {/* Account selector */}
          {activeAccounts.length > 0 && (
            <div>
              <label style={{ fontSize: 12, color: C.muted, marginBottom: 6, display: "block" }}>Viewing Account</label>
              <select
                value={dashboardAccountId || ""}
                onChange={e => setDashboardAccountId(e.target.value || null)}
                style={{
                  width: "100%", padding: 10,
                  background: "#1a1d2e", border: `1px solid ${C.border}`,
                  borderRadius: 8, color: C.text, fontSize: 13,
                }}
              >
                <option value="">All Accounts</option>
                {activeAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} — {acc.account_type?.toUpperCase()} (${acc.starting_balance})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Market Status — fed from shared calendar state, no internal fetch */}
          <MarketStatusCard
            marketStatus={marketStatus}
            nextHighImpact={nextHighImpact}
            calError={calError}
          />

          {/* Quick Actions */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            {[
              { view: "entry",     emoji: "✍️", label: "Log Trade",         sub: "Manual or CSV Import",   bg: C.green  },
              { view: "review",    emoji: "🧠", label: "Review Last Trade", sub: "AI Analysis & Feedback", bg: C.purple },
              { view: "checklist", emoji: "✅", label: "Can I Trade?",      sub: "Pre-Trade Checklist",    bg: C.blue   },
            ].map(({ view, emoji, label, sub, bg }) => (
              <Card
                key={view}
                style={{ cursor: "pointer", transition: "all 0.2s", padding: "9px 14px" }}
                onClick={() => setView(view)}
                onMouseEnter={e => (e.currentTarget.style.transform = "translateY(-2px)")}
                onMouseLeave={e => (e.currentTarget.style.transform = "translateY(0)")}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, background: bg + "20", borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0,
                  }}>
                    {emoji}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{label}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{sub}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Account Equity chart */}
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <SectionTitle>{chartView === "equity" ? "Account Equity" : "Net P&L"}</SectionTitle>
              <div style={{ display: "inline-flex", background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, padding: 2 }}>
                {[{ id: "equity", label: "Equity" }, { id: "net", label: "Net P&L" }].map(opt => {
                  const active = chartView === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setChartView(opt.id)}
                      style={{
                        padding: "4px 12px", border: "none", borderRadius: 6, fontSize: 11,
                        fontWeight: active ? 700 : 400, cursor: "pointer",
                        background: active ? C.blue : "transparent",
                        color: active ? "#fff" : C.muted,
                        transition: "all 0.15s",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time range tabs */}
            <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
              {["1W","1M","3M","YTD","All"].map(r => (
                <button
                  key={r}
                  onClick={() => setTimeRange(r)}
                  style={{
                    padding: "3px 10px", border: `1px solid ${timeRange === r ? C.blue : C.border}`,
                    borderRadius: 4, fontSize: 11, fontWeight: timeRange === r ? 700 : 400,
                    background: timeRange === r ? C.blue + "20" : "transparent",
                    color: timeRange === r ? C.blue : C.muted, cursor: "pointer",
                  }}
                >
                  {r}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 26, fontWeight: 800, color: chartView === "equity" ? C.text : (rangeNetPnl >= 0 ? C.green : C.red), fontFamily: "monospace" }}>
              {chartView === "equity"
                ? `$${totalEquity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : fmt$(rangeNetPnl)
              }
              {chartView === "equity" && (
                <div style={{ display: "inline-block", marginLeft: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: rangeNetPnl >= 0 ? C.green : C.red }}>
                    {fmt$(rangeNetPnl)} net {timeRange === "All" ? "all-time" : timeRange}
                  </div>
                  {totalPayouts > 0 && (
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>
                      −${totalPayouts.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} payouts
                    </div>
                  )}
                </div>
              )}
              {chartView === "net" && timeRange !== "All" && (
                <span style={{ fontSize: 14, fontWeight: 400, color: C.muted, marginLeft: 10 }}>
                  in {timeRange}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
              {chartView === "equity"
                ? `Starting balance: $${startingBalance.toLocaleString()}`
                : (timeRange === "All" ? "Net change since you started" : `Net change over the last ${timeRange}`)
              }
            </div>

            <div style={{ display: "flex", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
              {[
                { key: "showPayouts",      setter: setShowPayouts,       label: "Show Payouts",     color: "#f59e0b", active: showPayouts },
                { key: "showDrawdownLine", setter: setShowDrawdownLine,  label: "Drawdown Floor",   color: "#ef4444", active: showDrawdownLine, hide: !resolvedDrawdownLimit },
                { key: "showDailyLoss",    setter: setShowDailyLossLine, label: "Daily Loss Limit", color: "#f97316", active: showDailyLossLine, hide: !acctDailyLossLimit },
                { key: "showTargetGoal",   setter: setShowTargetGoalLine, label: "Target Goal",     color: "#22c55e", active: showTargetGoalLine, hide: !isEvalAccount || !acctTargetGoal },
              ].filter(o => !o.hide).map(opt => (
                <label key={opt.key} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 11, color: opt.active ? opt.color : C.muted, userSelect: "none" }}>
                  <input type="checkbox" checked={opt.active} onChange={() => opt.setter(v => !v)} style={{ accentColor: opt.color, cursor: "pointer" }} />
                  {opt.label}
                </label>
              ))}
            </div>

            <div style={{ height: 140 }}>
              {filteredEquityCurve.length > 1 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={(() => {
                    // For intraday trailing, floor is per-trade — merge by index
                    if (resolvedDrawdownType === "intraday_trailing" && showDrawdownLine) {
                      // drawdownFloorCurve mirrors analytics.equityCurve — offset by slice position
                      const offset = (analytics?.equityCurve?.length || 0) - filteredEquityCurve.length;
                      return filteredEquityCurve.map((p, i) => {
                        const payoutDeduction = payoutDeductionAsOf(p.fullDate);
                        const base = chartView === "equity" ? startingBalance + p.v - payoutDeduction : p.v;
                        return { date: p.date, v: base, floor: drawdownFloorCurve[offset + i]?.floor };
                      });
                    }
                    // EOD/static — use date-keyed map
                    const floorMap = {};
                    drawdownFloorCurve.forEach(d => { floorMap[d.date] = d.floor; });
                    return filteredEquityCurve.map(p => {
                      const date = String(p.date).slice(0, 5);
                      const payoutDeduction = payoutDeductionAsOf(p.fullDate);
                      const base = chartView === "equity" ? startingBalance + p.v - payoutDeduction : p.v;
                      return { date: p.date, v: base, floor: showDrawdownLine ? floorMap[date] : undefined };
                    });
                  })()}>
                    <defs>
                      <linearGradient id="hEqC" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={C.green} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={C.green} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" stroke={C.muted} fontSize={10} tickLine={false} axisLine={false} minTickGap={30} />
                    <YAxis
                      domain={(showDrawdownLine && resolvedDrawdownLimit > 0) || (showTargetGoalLine && isEvalAccount && acctTargetGoal > 0)
                        ? ([dataMin, dataMax]) => {
                            const floorVals = showDrawdownLine ? drawdownFloorCurve.map(d => d.floor).filter(Boolean) : [];
                            const minFloor = floorVals.length ? Math.min(...floorVals) : dataMin;
                            const maxWithTarget = showTargetGoalLine && isEvalAccount && acctTargetGoal > 0
                              ? Math.max(dataMax, targetGoalThreshold)
                              : dataMax;
                            const padding = (maxWithTarget - Math.min(dataMin, minFloor)) * 0.05;
                            return [Math.min(dataMin, minFloor) - padding, maxWithTarget + padding];
                          }
                        : ['auto', 'auto']
                      }
                      hide={true}
                    />
                    <Area type="monotone" dataKey="v" name={chartView === "equity" ? "Equity" : "Net P&L"} stroke={C.green} strokeWidth={2.5} fill="url(#hEqC)" />
                    {showDrawdownLine && resolvedDrawdownLimit > 0 && (
                      <Line type="monotone" dataKey="floor" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Drawdown Floor" />
                    )}
                    {showDailyLossLine && acctDailyLossLimit > 0 && (
                      <ReferenceLine y={dailyLossThreshold} stroke="#f97316" strokeDasharray="4 3" strokeWidth={1.5}
                        label={{ value: "Daily Loss Limit", position: "insideBottomRight", fill: "#f97316", fontSize: 10 }} />
                    )}
                    {showTargetGoalLine && isEvalAccount && acctTargetGoal > 0 && (
                      <ReferenceLine y={targetGoalThreshold} stroke="#22c55e" strokeDasharray="4 3" strokeWidth={1.5}
                        label={{ value: "Target Goal", position: "insideTopRight", fill: "#22c55e", fontSize: 10 }} />
                    )}
                    <Tooltip
                      labelFormatter={label => `Date: ${label}`}
                      formatter={(v, name) => [
                        `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        name,
                      ]}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 12 }}>
                  No trade history yet
                </div>
              )}
            </div>
          </Card>

          {/* ROI Summary */}
          <ROISummaryWidget
            userId={userId}
            accounts={accounts}
            setView={setView}
          />

          {/* Recent Trades */}
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <SectionTitle action={
                <button onClick={() => setView("analytics")} style={{ background: "none", border: "none", color: C.blue, fontSize: 12, cursor: "pointer" }}>
                  View Analytics →
                </button>
              }>
                Recent Trades ({selectedAccount ? selectedAccount.name : "All Accounts"})
              </SectionTitle>
              <button
                onClick={() => setShowRMultiple(!showRMultiple)}
                style={{
                  padding: "4px 8px",
                  background: showRMultiple ? C.blue : C.border,
                  color: C.text, border: "none", borderRadius: 4,
                  fontSize: 10, fontWeight: 600, cursor: "pointer",
                }}
              >
                {showRMultiple ? "R-Multiple" : "P&L"}
              </button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {["Date / Time", "Symbol", "Setup", "Dir", "Result", showRMultiple ? "R-Multiple" : "P&L", "Score"].map(h => (
                      <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: C.muted, fontWeight: 500, whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}` }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...accountTrades].sort((a, b) => {
                    if (a.exited_at && b.exited_at) {
                      const diff = new Date(b.exited_at) - new Date(a.exited_at);
                      if (diff !== 0) return diff;
                      return (Number(a.broker_trade_id) || 0) - (Number(b.broker_trade_id) || 0);
                    }
                    const realDate = (t) => {
                      const s = t.trade_date || t.created_at?.split("T")[0] || "";
                      const h = parseInt((t.exit_time || t.entry_time || "00").slice(0, 2), 10);
                      if (s && !isNaN(h) && h >= 17) {
                        const d = new Date(s + "T12:00:00"); d.setDate(d.getDate() - 1);
                        return d.toISOString().slice(0, 10);
                      }
                      return s;
                    };
                    const dA = `${realDate(a)} ${a.exit_time || a.entry_time || "00:00"}`;
                    const dB = `${realDate(b)} ${b.exit_time || b.entry_time || "00:00"}`;
                    if (dB !== dA) return dB.localeCompare(dA);
                    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                  }).slice(0, 5).map(t => (
                    <tr
                      key={t.id}
                      onClick={() => openTradeReview(t)}
                      style={{ borderBottom: `1px solid ${C.border}20`, cursor: "pointer", backgroundColor: "transparent" }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = C.border + "20")}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <td style={{ padding: "8px 10px", color: C.sub, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        {t.trade_date || t.created_at?.split("T")[0] || "—"}
                        {isSessionShifted(t) && <span title="Session day" style={{ marginLeft: 4, opacity: 0.7 }}>🌙</span>}
                        <br />
                        <span style={{ fontSize: 10 }}>{(t.entry_time || t.trade_time || "")?.slice(0, 5) || "—"}</span>
                      </td>
                      <td style={{ padding: "8px 10px", color: C.text, fontFamily: "monospace", fontWeight: 600 }}>{t.symbol}</td>
                      <td style={{ padding: "8px 10px" }}><Tag label={t.setup_type || "—"} color={C.purple} /></td>
                      <td style={{ padding: "8px 10px", color: t.direction === "Long" ? C.green : C.red, fontWeight: 600 }}>
                        {t.direction === "Long" ? "↑" : "↓"} {t.direction}
                      </td>
                      <td style={{ padding: "8px 10px" }}>
                        <span style={{
                          background: t.result === "Win" ? C.green + "18" : C.red + "18",
                          color: t.result === "Win" ? C.green : C.red,
                          padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                        }}>
                          {t.result}
                        </span>
                      </td>
                      <td style={{ padding: "8px 10px" }}>
                        {showRMultiple ? (() => {
                          let rMultiple = null;
                          if (t.entry_price && t.stop_loss) {
                            const risk = Math.abs(t.entry_price - t.stop_loss);
                            rMultiple = risk > 0 ? t.pnl / risk : null;
                          }
                          if (rMultiple === null && t.risk) rMultiple = t.pnl / t.risk;
                          return rMultiple !== null ? (
                            <span style={{ color: rMultiple > 0 ? C.green : C.red, fontWeight: 600, fontFamily: "monospace" }}>
                              {rMultiple > 0 ? "+" : ""}{rMultiple.toFixed(1)}R
                            </span>
                          ) : (
                            <span style={{ color: C.muted, fontFamily: "monospace" }}>—</span>
                          );
                        })() : <Pill value={t.pnl || 0} />}
                      </td>
                      <td style={{
                        padding: "8px 10px", fontFamily: "monospace",
                        color: !t.discipline_score ? C.muted
                          : t.discipline_score >= 8 ? C.green
                          : t.discipline_score >= 6 ? C.yellow
                          : C.red,
                      }}>
                        {t.discipline_score ? `${t.discipline_score}/10` : "—"}
                      </td>
                      <td style={{ padding: "8px 10px" }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setShareTarget(t); }}
                          title="Share trade"
                          style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: "2px 6px", borderRadius: 4 }}
                          onMouseEnter={e => e.currentTarget.style.color = "#4caf7d"}
                          onMouseLeave={e => e.currentTarget.style.color = C.muted}
                        >↑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

        </div>
      </div>

      {/* ── DRAG HANDLE: center | right ── desktop only ──────────────── */}
      {isDesktop ? (
        <div
          onMouseDown={rightHandleDown}
          style={{
            cursor: "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = C.border + "40")}
          onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
        >
          <div style={{ width: 2, height: 40, borderRadius: 2, background: C.border, opacity: 0.5 }} />
        </div>
      ) : null}

      {/* ── RIGHT COLUMN ──────────────────────────────────────────────── */}
      <div style={{ minHeight: 0, overflow: "hidden" }}>
        <div style={colScroll}>
          {/* Calendar fed from shared state — no internal fetch */}
          <EconomicCalendarWidget
            todayEvents={todayEvents}
            eventsByDay={eventsByDay}
            calLoading={calLoading}
            dataTrustworthy={dataTrustworthy}
            calError={calError}
          />
          <MarketSentimentWidget />
          <TraderSentimentWidget
            accountTrades={accountTrades}
            accountRules={accountRules}
            profile={profile}
            totalEquity={totalEquity}
            startingBalance={startingBalance}
          />
          <NewsWidget />
        </div>
      </div>
      {shareTarget && (
        <TradeShareModal
          trade={shareTarget}
          allTrades={accountTrades}
          onClose={() => setShareTarget(null)}
        />
      )}
    </div>
    </div>
  );
}
